import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { Panel, PageHeader, Loading, StatusPill } from "@/components/portal/widgets";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ImageOff, Loader2, CheckCircle2 } from "lucide-react";
import { fmtDateTime } from "@/lib/time";
import { useLiveRefresh, UpdatedAgo } from "@/lib/liveRefresh";

const urlCache = new Map();

// Fetches the photo with the user's login and shows it as a local blob (never a public link).
const Photo = ({ path, label, onOpen }) => {
  const [src, setSrc] = useState(urlCache.get(path) || null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (src) return;
    let alive = true;
    fleetApi.get(`/files/${path}`, { responseType: "blob" })
      .then((r) => { const u = URL.createObjectURL(r.data); urlCache.set(path, u); if (alive) setSrc(u); })
      .catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [path, src]);
  if (err) return <div className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-md border border-slate-700 bg-slate-900 text-[10px] text-slate-500"><ImageOff className="h-4 w-4" />Couldn't load</div>;
  if (!src) return <div className="flex h-24 w-24 items-center justify-center rounded-md border border-slate-800 bg-slate-900"><Loader2 className="h-4 w-4 animate-spin text-slate-500" /></div>;
  return (
    <button onClick={() => onOpen({ src, label })} className="overflow-hidden rounded-md border border-slate-700 hover:border-blue-500" data-testid={`insp-photo-${path.split("/").pop()}`}>
      <img src={src} alt={label} className="h-24 w-24 object-cover" />
    </button>
  );
};

const InspectionCard = ({ insp, onOpen, onReviewed }) => {
  const [busy, setBusy] = useState(false);
  const all = [...(insp.items || []), ...(insp.equipment || [])];
  const photos = all.filter((i) => i.photo);
  const fails = all.filter((i) => /fail|missing|absent/i.test(i.status || ""));
  const review = async () => {
    setBusy(true);
    try { await fleetApi.post(`/inspections/${insp.id}/review`, {}); toast.success("Inspection marked reviewed."); onReviewed(insp.id); }
    catch (e) { toast.error(fleetError(e, "Couldn't mark reviewed.")); }
    finally { setBusy(false); }
  };
  return (
    <li className="px-5 py-4" data-testid={`inspection-${insp.id}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white">
            <Link to={`/portal/drivers/${insp.driver_id}`} className="hover:text-blue-300 hover:underline">{insp.driver_name}</Link>
            <span className="ml-2 font-mono-data text-[11px] font-normal text-slate-500">Vehicle {insp.vehicle_fleet || insp.vehicle_id || "—"} · {fmtDateTime(insp.created_at)}</span>
          </p>
          <p className="text-xs text-slate-400">{all.length} checks · {photos.length} photo{photos.length === 1 ? "" : "s"}{insp.overall_note ? ` · “${insp.overall_note}”` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill value={fails.length ? `${fails.length} failed` : "passed"} />
          {insp.reviewed ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400" data-testid={`inspection-reviewed-${insp.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Reviewed</span>
            : <Button size="sm" variant="outline" disabled={busy} onClick={review} className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white" data-testid={`inspection-review-${insp.id}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Mark reviewed"}</Button>}
        </div>
      </div>
      {fails.length > 0 && <p className="mt-2 text-xs text-red-300">Failed: {fails.map((f) => f.label).join(", ")}</p>}
      {photos.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {photos.map((p) => <div key={p.photo} className="space-y-1"><Photo path={p.photo} label={p.label} onOpen={onOpen} /><p className="w-24 truncate text-[10px] text-slate-500">{p.label}</p></div>)}
        </div>
      )}
    </li>
  );
};

export default function InspectionsPage() {
  const [list, setList] = useState(null);
  const [big, setBig] = useState(null);
  const load = useCallback(() => fleetApi.get("/inspections").then((r) => setList(Array.isArray(r.data) ? r.data : [])), []);
  useEffect(() => { load().catch((e) => { toast.error(fleetError(e, "Couldn't load inspections.")); setList([]); }); }, [load]);
  const updatedAt = useLiveRefresh(load, 60000);
  if (!list) return <Loading />;
  return (
    <div data-testid="inspections-page">
      <PageHeader title="Vehicle Inspections" subtitle={`${list.length} inspection${list.length === 1 ? "" : "s"} submitted by drivers`}>
        <UpdatedAgo at={updatedAt} testid="inspections-live-updated" />
      </PageHeader>
      <Panel testid="inspections-list">
        {list.length === 0 ? <p className="px-5 py-12 text-center text-sm text-slate-400" data-testid="inspections-empty">No inspections submitted yet.</p> : (
          <ul className="divide-y divide-slate-800">
            {list.map((i) => <InspectionCard key={i.id} insp={i} onOpen={setBig} onReviewed={(id) => setList((p) => p.map((x) => (x.id === id ? { ...x, reviewed: true } : x)))} />)}
          </ul>
        )}
      </Panel>
      <Dialog open={!!big} onOpenChange={(v) => { if (!v) setBig(null); }}>
        <DialogContent className="border-slate-800 bg-[#0f172a] text-white sm:max-w-3xl" data-testid="inspection-photo-dialog">
          <DialogTitle>{big?.label}</DialogTitle>
          <DialogDescription className="sr-only">Inspection photo</DialogDescription>
          {big && <img src={big.src} alt={big.label} className="max-h-[70vh] w-full rounded-md object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
